import { createContext, useCallback, useContext, useEffect, useId, useState, type ReactNode } from "react";

interface GlobalLoadingContextValue {
  isLoading: boolean;
  setLoading: (key: string, loading: boolean) => void;
}

const GlobalLoadingContext = createContext<GlobalLoadingContextValue>({
  isLoading: false,
  setLoading: () => {},
});

export function GlobalLoadingProvider({ children }: { children: ReactNode }) {
  const [activeKeys, setActiveKeys] = useState<Set<string>>(new Set());

  const setLoading = useCallback((key: string, loading: boolean) => {
    setActiveKeys((prev) => {
      const has = prev.has(key);
      if (loading === has) return prev;
      const next = new Set(prev);
      if (loading) next.add(key); else next.delete(key);
      return next;
    });
  }, []);

  return (
    <GlobalLoadingContext.Provider value={{ isLoading: activeKeys.size > 0, setLoading }}>
      {children}
    </GlobalLoadingContext.Provider>
  );
}

/** Whether any registered source is currently loading — drives GlobalLoadingBar. */
export function useIsGlobalLoading() {
  return useContext(GlobalLoadingContext).isLoading;
}

/**
 * Register this component's loading state with the shared right-panel
 * indicator (GlobalLoadingBar in AppLayout/SuperAdminLayout) instead of
 * rendering a local "Cargando..." spinner. Each call site gets its own
 * stable key automatically, so multiple lists loading at once compose
 * correctly — the bar shows as long as ANY of them is still loading.
 */
export function useGlobalLoading(loading: boolean) {
  const key = useId();
  const { setLoading } = useContext(GlobalLoadingContext);
  useEffect(() => {
    setLoading(key, loading);
    return () => setLoading(key, false);
  }, [loading, key, setLoading]);
}
